import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  getPrinterRoutes,
  getSavedPrinter,
  hasPrintBridge,
  listPrinters,
  savePrinter,
  savePrinterRoutes,
  sendToPrinter,
} from "../utils/printBridge";
import { buildReceiptPayload } from "../utils/printReceipt";
import { categoryArray, useCategories } from "../utils/useCategories";
import { ProFeatureModal, useIsPro } from "../../components/ProGate";
import { cashierAxios } from "../../axiosInstance";
import PrinterAssignments from "./PrinterAssignments";
import { LuPrinter, LuX } from "react-icons/lu";

// The main printer prints the customer receipt (all plans). On the Pro plan,
// extra printers can also print kitchen/bar slips or customer receipts (see
// printExtraPrinters in printReceipt.js).
function PrinterSettingsModal({ onClose, template, currency }) {
  const bridge = hasPrintBridge();
  const [printers, setPrinters] = useState([]);
  const [selected, setSelected] = useState(getSavedPrinter() || "");
  const [loading, setLoading] = useState(bridge);
  const [error, setError] = useState("");
  const [testing, setTesting] = useState(false);

  const { isLoading: planLoading, isPro } = useIsPro(cashierAxios, "cashier", bridge);
  const { data: categoryData } = useCategories();
  const categories = categoryArray(categoryData);
  const [routes, setRoutes] = useState(getPrinterRoutes);
  const [showProInfo, setShowProInfo] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setPrinters(await listPrinters());
    } catch (err) {
      setError(err?.message || "Couldn't load printers.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (bridge) load();
  }, [bridge, load]);

  // A saved printer that Windows no longer reports (renamed, removed).
  const savedMissing =
    !loading && selected && !printers.some((p) => p.name === selected);

  // A slip that never prints can't be saved (it needs draft and/or paid).
  const routesInvalid =
    isPro && routes.some((r) => !r.printOn.draft && !r.printOn.paid);

  const handleSave = () => {
    savePrinter(selected || null);
    // Free stores keep their saved routes untouched; they just aren't used.
    if (isPro) savePrinterRoutes(routes);
    toast.success(
      selected ? `Receipts will print to ${selected}.` : "Receipt printer cleared."
    );
    onClose();
  };

  const handleTestPrint = async () => {
    if (!selected) return;
    setTesting(true);
    try {
      const now = new Date();
      const payload = buildReceiptPayload({
        receipt: {
          orderNo: "TEST",
          date: now.toLocaleDateString(),
          time: now.toLocaleTimeString(),
          items: [{ name: "Test item", quantity: 1, total: 0 }],
          autoBill: [],
          total: 0,
          paymentType: "Test print",
        },
        template,
        currency,
        kind: "reprint",
        printerName: selected,
      });
      payload.copies = 1;
      const result = await sendToPrinter(payload);
      toast.success(`Test sent to ${selected} (${result?.status || "sent"}).`);
    } catch (err) {
      toast.error(`Test print failed: ${err?.message || "unknown error"}`);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-white text-black rounded-2xl shadow-xl w-full max-w-xl mx-4 max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center gap-3 border-b border-gray-200 px-6 py-4">
          <LuPrinter className="h-7 w-7 text-blue-600" aria-hidden="true" />
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-semibold text-slate-900">Printer settings</h2>
            <p className="text-sm text-gray-500">
              Choose the printer receipts go to on this till.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            title="Close"
            aria-label="Close printer settings"
          >
            <LuX className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto bg-gray-50 px-6 py-4">
          {!bridge && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm">
              01POS desktop app is not running, so we can’t find your printers.
              You can still print receipts as usual. Open 01POS on your desktop
              to connect and print to multiple printers.
            </div>
          )}

          {bridge && (
            <>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-slate-700">Receipt printer</span>
                <button
                  onClick={load}
                  disabled={loading}
                  className="text-sm text-blue-600 hover:underline disabled:text-gray-400"
                >
                  {loading ? "Loading..." : "Refresh"}
                </button>
              </div>

              {error && (
                <div className="rounded-xl border border-red-300 bg-red-50 p-3 text-sm mb-3">
                  {error}
                </div>
              )}

              {!loading && !error && printers.length === 0 && (
                <div className="rounded-xl border border-gray-200 bg-white p-3 text-sm mb-3">
                  No printers found. Install the printer's Windows driver, then
                  Refresh.
                </div>
              )}

              <PrinterAssignments
                printers={printers}
                loading={loading}
                selected={selected}
                setSelected={setSelected}
                routes={routes}
                setRoutes={setRoutes}
                categories={categories}
                canAssignExtras={isPro}
              />

              {savedMissing && (
                <p className="text-sm text-amber-700 mb-3">
                  The saved printer "{selected}" wasn't found on this computer.
                  Pick another one.
                </p>
              )}

              {selected && (
                <button
                  onClick={handleTestPrint}
                  disabled={testing || savedMissing}
                  className="text-sm text-blue-600 hover:underline disabled:text-gray-400 mb-2"
                >
                  {testing ? "Sending test..." : "Print a test receipt"}
                </button>
              )}

              {planLoading ? (
                <p className="text-sm text-gray-500">Checking your plan...</p>
              ) : (
                !isPro && (
                  <div className="rounded-xl border border-gray-200 bg-white p-3 text-sm text-gray-600">
                    Extra printers is a Pro-plan feature. Assign categories to kitchen
                    or bar printers with Pro.{" "}
                    <button
                      className="text-blue-700 font-semibold underline ml-1"
                      onClick={() => setShowProInfo(true)}
                    >
                      Learn more
                    </button>
                  </div>
                )
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg border border-gray-300 bg-white font-medium text-slate-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          {bridge && (
            <button
              onClick={handleSave}
              disabled={loading || savedMissing || routesInvalid}
              className="px-5 py-2 rounded-lg bg-blue-600 font-medium text-white hover:bg-blue-700 disabled:bg-gray-300"
            >
              Save
            </button>
          )}
        </div>
      </div>
      <ProFeatureModal
        open={showProInfo}
        onClose={() => setShowProInfo(false)}
        title="Extra printers"
      >
        <p className="mt-3">
          Print to more than one printer from the same till, automatically.
        </p>
        <ul className="mt-3 ml-5 list-disc text-gray-600 text-sm">
          <li>Send kitchen or bar slips with just their items, no prices</li>
          <li>Print a second customer receipt on another printer</li>
          <li>Print on draft, after a paid sale, or both</li>
        </ul>
      </ProFeatureModal>
    </div>
  );
}

export default PrinterSettingsModal;
